import axiosInstance from '../config/axios';
import { API_ENDPOINTS } from '../config/api';

const POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_TIMEOUT_MS = 20 * 60 * 1000;
const ARGUMENTS_POLL_TIMEOUT_MS = 30 * 60 * 1000;
const STATUS_RETRYABLE_CODES = new Set([500, 502, 503, 504]);
const STATUS_MAX_RETRIES = 4;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const SOURCE_DOCUMENT_JOB_TYPES = {
  ADMIN_POPULATE: 'admin-populate',
  INTAKE_PARSE: 'intake-parse',
  ARGUMENTS: 'arguments',
  ICF_GENERATE: 'icf-generate',
  IND_GENERATE: 'ind-generate',
};

export async function startSourceDocumentJob(jobType, body) {
  const response = await axiosInstance.post(
    API_ENDPOINTS.ai.sourceDocumentJobs,
    { ...body, jobType },
    { timeout: 60000 }
  );
  return response.data;
}

export async function getSourceDocumentJobStatus(jobId) {
  let lastError;
  for (let attempt = 0; attempt <= STATUS_MAX_RETRIES; attempt += 1) {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.ai.sourceDocumentJobStatus(jobId),
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      if (STATUS_RETRYABLE_CODES.has(status) && attempt < STATUS_MAX_RETRIES) {
        await sleep(1000 * (attempt + 1));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

export async function pollSourceDocumentJob(jobId, { onProgress, pollTimeoutMs } = {}) {
  const startedAt = Date.now();
  const timeoutMs = pollTimeoutMs || DEFAULT_POLL_TIMEOUT_MS;

  while (Date.now() - startedAt < timeoutMs) {
    const data = await getSourceDocumentJobStatus(jobId);
    const job = data?.job;
    if (!job) {
      throw new Error('Invalid job status response');
    }

    if (typeof onProgress === 'function') {
      onProgress(job);
    }

    if (job.status === 'completed') {
      if (!job.result?.success) {
        const error = new Error(job.result?.error || job.error || 'Job failed');
        error.response = { data: { ...job.result, steps: job.steps } };
        throw error;
      }
      return job.result;
    }

    if (job.status === 'failed') {
      const error = new Error(job.error || job.result?.error || 'Job failed');
      error.response = {
        data: {
          ...(job.result || {}),
          steps: job.steps,
          error: job.error || job.result?.error,
        },
      };
      throw error;
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  throw new Error(
    'Processing is taking longer than expected. Please wait a moment and retry, or contact support if this persists.'
  );
}

export async function runSourceDocumentJob(jobType, body, options = {}) {
  const start = await startSourceDocumentJob(jobType, body);
  if (!start?.success || !start.jobId) {
    throw new Error(start?.error || 'Failed to start background processing job');
  }
  const pollTimeoutMs =
    options.pollTimeoutMs ||
    (jobType === SOURCE_DOCUMENT_JOB_TYPES.ARGUMENTS
      ? ARGUMENTS_POLL_TIMEOUT_MS
      : DEFAULT_POLL_TIMEOUT_MS);
  return pollSourceDocumentJob(start.jobId, { ...options, pollTimeoutMs });
}

export default {
  SOURCE_DOCUMENT_JOB_TYPES,
  startSourceDocumentJob,
  getSourceDocumentJobStatus,
  pollSourceDocumentJob,
  runSourceDocumentJob,
};
